import { inject, Injectable } from "@angular/core";
import { AdditionalCostDetailDto, AdditionalCostDto, AdditionalCostRejectionType, PaginatedAdditionalCostsResponse } from "@dtos/additional-cost.dto";
import { ApiService } from "@services/api.service";
import { EndPoints } from "@services/EndPoints";

@Injectable({ providedIn: 'root' })
export class AdditionalCostsProvider {

  private readonly api = inject(ApiService);

  /**
   * OBTENER GASTOS ADICIONALES CON SOPORTE PARA FILTRADO, BUSQUEDA, ORDENAMIENTO Y PAGINACION
   * @param queryParams - OBJETO CON TODOS LOS PARAMETROS DE CONSULTA
   * @returns RESPUESTA PAGINADA CON GASTOS ADICIONALES Y TOTAL
   */
  async getAdditionalCosts(queryParams: Record<string, any> = {}): Promise<PaginatedAdditionalCostsResponse | null> {
    try {
      //ESTABLECER VALORES POR DEFECTO SI NO SE PROPORCIONAN
      const params = {
        page: 1,
        page_size: 50,
        ...queryParams
      };

      //REALIZAR LA PETICION AL BACKEND
      const response = await this.api.get<PaginatedAdditionalCostsResponse>({
        url: EndPoints.getAdditionalCosts,
        queryParams: params
      });

      if (response) {
        //TRANSFORMAR RESULTADOS A INSTANCIAS DE AdditionalCostDto
        return {
          ...response,
          results: response.results.map(item => AdditionalCostDto.fromJson(item))
        };
      }

      return response;
    } catch (error) {
      console.error('Error al obtener gastos adicionales:', error);
      return null;
    }
  }

  /**
   * OBTENER UN GASTO ADICIONAL POR ID
   * @param additionalCostId - ID DEL GASTO ADICIONAL
   * @returns DATOS DEL GASTO ADICIONAL CON DETALLE
   */
  async getAdditionalCostById(additionalCostId: number): Promise<AdditionalCostDetailDto | null> {
    try {
      const response = await this.api.get<AdditionalCostDetailDto>({
        url: EndPoints.getAdditionalCost.replace('{additionalCostId}', String(additionalCostId))
      });
      return response || null;
    } catch (error) {
      console.error('Error al obtener gasto adicional por ID:', error);
      return null;
    }
  }

  /**
   * CREAR UN NUEVO GASTO ADICIONAL
   * @param data - DATOS DEL GASTO ADICIONAL A CREAR
   * @returns GASTO ADICIONAL CREADO
   */
  async createAdditionalCost(data: Partial<AdditionalCostDto>): Promise<AdditionalCostDto | null> {
    try {
      const formParams: Record<string, any> = { ...data };

      const response = await this.api.post<AdditionalCostDto>({
        url: EndPoints.createAdditionalCost,
        formParams
      });

      return response ? AdditionalCostDto.fromJson(response) : null;
    } catch (error) {
      console.error('Error al crear gasto adicional:', error);
      return null;
    }
  }

  /**
   * ACTUALIZAR UN GASTO ADICIONAL EXISTENTE
   * @param additionalCostId - ID DEL GASTO ADICIONAL A ACTUALIZAR
   * @param data - DATOS A ACTUALIZAR
   * @returns GASTO ADICIONAL ACTUALIZADO
   */
  async updateAdditionalCost(additionalCostId: number, data: Partial<AdditionalCostDto>): Promise<AdditionalCostDto | null> {
    try {
      const formParams: Record<string, any> = { ...data };

      const response = await this.api.patch<AdditionalCostDto>({
        url: EndPoints.updateAdditionalCost.replace('{additionalCostId}', String(additionalCostId)),
        formParams
      });

      return response ? AdditionalCostDto.fromJson(response) : null;
    } catch (error) {
      console.error('Error al actualizar gasto adicional:', error);
      return null;
    }
  }

  /**
   * ELIMINAR UN GASTO ADICIONAL
   * @param additionalCostId - ID DEL GASTO ADICIONAL A ELIMINAR
   * @returns TRUE SI SE ELIMINO CORRECTAMENTE, FALSE SI HUBO ERROR
   */
  async deleteAdditionalCost(additionalCostId: number): Promise<boolean> {
    try {
      await this.api.delete({
        url: EndPoints.deleteAdditionalCost.replace('{additionalCostId}', String(additionalCostId))
      });
      return true;
    } catch (error) {
      console.error('Error al eliminar gasto adicional:', error);
      return false;
    }
  }

  /**
   * EXPORTAR GASTOS ADICIONALES A CSV
   * DESCARGA UN ARCHIVO CSV CON LOS GASTOS ADICIONALES FILTRADOS
   * @param queryParams - PARAMETROS DE FILTRADO Y ORDENAMIENTO
   * @returns BLOB DEL ARCHIVO CSV
   */
  async exportAdditionalCostsCSV(queryParams: Record<string, any> = {}): Promise<Blob | null> {
    try {
      const blob = await this.api.downloadFile({
        url: EndPoints.exportAdditionalCostsCSV,
        queryParams
      });

      // CREAR UN ENLACE TEMPORAL PARA DESCARGAR EL ARCHIVO
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'additional-costs.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return blob;
    } catch (error) {
      console.error('Error al exportar gastos adicionales a CSV:', error);
      return null;
    }
  }

  /**
   * OBTENER TIPOS DE RECHAZO PARA GASTOS ADICIONALES
   * @returns LISTA DE TIPOS DE RECHAZO
   */
  async getRejectionTypes(): Promise<AdditionalCostRejectionType[]> {
    try {
      const response = await this.api.get<AdditionalCostRejectionType[]>({
        url: EndPoints.getAdditionalCostsRejectionTypes
      });

      return response || [];
    } catch (error) {
      console.error('Error al obtener tipos de rechazo:', error);
      return [];
    }
  }

  /**
   * RECHAZAR UNA SOLICITUD DE GASTO ADICIONAL
   * @param additionalCostId - ID DEL GASTO ADICIONAL
   * @param rejectionReason - MOTIVO DE RECHAZO
   * @param blockNewRequests - BLOQUEAR NUEVAS SOLICITUDES DE ESTE TIPO
   * @returns TRUE SI SE RECHAZÓ CORRECTAMENTE, FALSE SI HUBO ERROR
   */
  async rejectRequest(
    additionalCostId: number,
    rejectionReason: string,
    blockNewRequests: boolean,
    adminComments: string
  ): Promise<boolean> {
    try {
      await this.api.post({
        url: EndPoints.rejectAdditionalCostRequest.replace('{additionalCostId}', String(additionalCostId)),
        body: {
          rejection_reason: rejectionReason,
          admin_comments: adminComments,
          reject_similar: blockNewRequests
        }
      });
      return true;
    } catch (error) {
      console.error('Error al rechazar solicitud de gasto adicional:', error);
      return false;
    }
  }

  /**
   * RECHAZAR EL TICKET DE UN GASTO ADICIONAL
   * @param additionalCostId - ID DEL GASTO ADICIONAL
   * @param rejectionReason - MOTIVO DE RECHAZO
   * @returns TRUE SI SE RECHAZÓ CORRECTAMENTE, FALSE SI HUBO ERROR
   */
  async rejectTicket(
    additionalCostId: number,
    rejectionReason: string, 
    adminComments: string,
    blockNewRequests: boolean
  ): Promise<boolean> {
    try {
      await this.api.post({
        url: EndPoints.rejectAdditionalCostTicket.replace('{additionalCostId}', String(additionalCostId)),
        formParams: {
          rejection_reason: rejectionReason,
          admin_comments: adminComments,
          reject_similar: blockNewRequests
        }
      });
      return true;
    } catch (error) {
      console.error('Error al rechazar ticket de gasto adicional:', error);
      return false;
    }
  }

  /**
   * APROBAR UNA SOLICITUD DE GASTO ADICIONAL
   * @param additionalCostId - ID DEL GASTO ADICIONAL
   * @param adminComments - COMENTARIOS DEL ADMINISTRADOR (OPCIONAL)
   * @returns TRUE SI SE APROBÓ CORRECTAMENTE, FALSE SI HUBO ERROR
   */
  async approveRequest(
    additionalCostId: number,
    excludeFromInvoice: boolean,
    adminComments: string,
    blockNewRequests: boolean
  ): Promise<boolean> {
    try {
      await this.api.post({
        url: EndPoints.approveAdditionalCostRequest.replace('{additionalCostId}', String(additionalCostId)),
        body: {
          admin_comments: adminComments,
          not_invoiceable: excludeFromInvoice,
          reject_similar: blockNewRequests
        }
      });
      return true;
    } catch (error) {
      console.error('Error al aprobar solicitud de gasto adicional:', error);
      return false;
    }
  }

  /**
   * APROBAR EL TICKET DE UN GASTO ADICIONAL
   * @param additionalCostId - ID DEL GASTO ADICIONAL
   * @param adminComments - COMENTARIOS DEL ADMINISTRADOR (OPCIONAL)
   * @returns OBJETO CON SUCCESS Y ERROR MESSAGE SI FALLA
   */
  async approveTicket(
    additionalCostId: number,
    excludeFromInvoice: boolean,
    adminComments: string,
    blockNewRequests: boolean
  ): Promise<{ success: boolean; errorMessage?: string }> {
    try {
      await this.api.post({
        url: EndPoints.approveAdditionalCostTicket.replace('{additionalCostId}', String(additionalCostId)),
        body: {
          admin_comments: adminComments,
          not_invoiceable: excludeFromInvoice,
          reject_similar: blockNewRequests
        }
      });
      return { success: true };
    } catch (error: any) {
      console.error('Error al aprobar ticket de gasto adicional:', error);

      //EXTRAER MENSAJE DE ERROR DEL BACKEND SI EXISTE
      let errorMessage: string | undefined;
      if (error?.error?.message) {
        errorMessage = error.error.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      return { success: false, errorMessage };
    }
  }
}
